import React, { useState, useEffect } from 'react';
import {
  XCircle, DollarSign, MessageSquare, FileText, Filter, Download,
  Clock, CheckCircle, AlertTriangle, TrendingDown, RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminCancellationsRefunds = () => {
  const [cancellations, setCancellations] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const [selectedTab, setSelectedTab] = useState('cancellations');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [followUpMessage, setFollowUpMessage] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundMethod, setRefundMethod] = useState('stripe');
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, withCredentials: true };

      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/api/admin/cancellations/stats?days=${dateRange}`, { headers });
      setStats(statsRes.data);

      if (selectedTab === 'cancellations') {
        // Fetch cancellations
        const cancelRes = await axios.get(`${API_URL}/api/admin/cancellations?days=${dateRange}`, { headers });
        setCancellations(cancelRes.data.cancellations || []);
      } else {
        // Fetch refunds
        const refundRes = await axios.get(`${API_URL}/api/admin/refunds`, { headers });
        setRefunds(refundRes.data.refunds || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = async () => {
    if (!followUpMessage.trim()) {
      toast({
        title: "Error",
        description: "Please enter a follow-up message",
        variant: "destructive",
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/cancellations/${selectedItem._id}/follow-up`,
        null,
        {
          params: { message: followUpMessage },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      toast({
        title: "Success",
        description: "Follow-up recorded successfully",
      });

      setShowFollowUpModal(false);
      setFollowUpMessage('');
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to record follow-up",
        variant: "destructive",
      });
    }
  };

  const handleAddNotes = async (cancellationId) => {
    if (!adminNotes.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/cancellations/${cancellationId}/notes`,
        null,
        {
          params: { notes: adminNotes },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      toast({
        title: "Success",
        description: "Notes added successfully",
      });

      setAdminNotes('');
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add notes",
        variant: "destructive",
      });
    }
  };

  const handleProcessRefund = async () => {
    if (!refundAmount || parseFloat(refundAmount) <= 0) {
      toast({
        title: "Error",
        description: "Please enter a valid refund amount",
        variant: "destructive",
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/refunds/process`,
        null,
        {
          params: {
            cancellation_id: selectedItem._id,
            amount: parseFloat(refundAmount),
            method: refundMethod
          },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      toast({
        title: "Success",
        description: "Refund initiated successfully",
      });

      setShowRefundModal(false);
      setRefundAmount('');
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process refund",
        variant: "destructive",
      });
    }
  };

  const handleUpdateRefundStatus = async (refundId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/refunds/${refundId}/status`,
        null,
        {
          params: { status },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      toast({
        title: "Success",
        description: `Refund marked as ${status}`,
      });

      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update refund status",
        variant: "destructive",
      });
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-mint-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Cancellations & Refunds</h1>
              <p className="text-gray-600 mt-1">Manage premium subscription cancellations and refunds</p>
            </div>
            
            <div className="flex gap-3 mt-4 md:mt-0">
              <select 
                value={dateRange}
                onChange={(e) => setDateRange(Number(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"
              >
                <option value={7}>Last 7 Days</option>
                <option value={30}>Last 30 Days</option>
                <option value={90}>Last 90 Days</option>
              </select>

              <Button onClick={fetchData} className="bg-gray-800 hover:bg-gray-900">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 mt-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Cancellations</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalCancellations || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Last {dateRange} days</p>
                </div>
                <div className="bg-cream-100 p-3 rounded-lg">
                  <XCircle className="w-6 h-6 text-navy-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Refunds</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalRefunds || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Processed refunds</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600">Refund Amount</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">${stats?.totalRefundAmount?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-gray-500 mt-1">Total refunded</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <TrendingDown className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600">Top Reason</p>
                  <p className="text-lg font-bold text-gray-900 mt-1">
                    {stats?.topReasons && stats.topReasons.length > 0 ? stats.topReasons[0].reason : 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats?.topReasons && stats.topReasons.length > 0 ? `${stats.topReasons[0].count} times` : ''}
                  </p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b">
            <div className="flex">
              <button
                onClick={() => setSelectedTab('cancellations')}
                className={`px-6 py-4 font-medium ${
                  selectedTab === 'cancellations'
                    ? 'text-navy-500 border-b-2 border-mint-500'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Cancelled Subscriptions
              </button>
              <button
                onClick={() => setSelectedTab('refunds')}
                className={`px-6 py-4 font-medium ${
                  selectedTab === 'refunds'
                    ? 'text-navy-500 border-b-2 border-mint-500'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Refunded Users
              </button>
            </div>
          </div>

          <div className="p-6">
            {selectedTab === 'cancellations' && (
              <div className="space-y-4">
                {cancellations.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No cancellations found</p>
                ) : (
                  cancellations.map((cancel) => (
                    <Card key={cancel._id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg">{cancel.userName}</h3>
                              {cancel.followedUp && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                  Followed Up
                                </span>
                              )}
                              {cancel.refunded && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                  Refunded
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{cancel.userEmail}</p>
                            <p className="text-sm text-gray-500 mt-1">
                              Plan: <span className="font-medium">{cancel.planName || 'N/A'}</span>
                            </p>
                            <p className="text-sm text-gray-500">
                              Cancelled: {cancel.cancelledAt ? format(new Date(cancel.cancelledAt), 'PPp') : 'N/A'}
                            </p>
                            <p className="text-sm text-gray-700 mt-2">
                              <span className="font-medium">Reason:</span> {cancel.reason}
                            </p>
                            {cancel.adminNotes && (
                              <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded">
                                <span className="font-medium">Admin Notes:</span> {cancel.adminNotes}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex gap-2 ml-4">
                            {!cancel.followedUp && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedItem(cancel);
                                  setShowFollowUpModal(true);
                                }}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                <MessageSquare className="w-4 h-4 mr-1" />
                                Follow Up
                              </Button>
                            )}
                            {!cancel.refunded && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedItem(cancel);
                                  setShowRefundModal(true);
                                }}
                                className="bg-orange-600 hover:bg-orange-700"
                              >
                                <DollarSign className="w-4 h-4 mr-1" />
                                Process Refund
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Add Notes */}
                        {!cancel.adminNotes && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add admin notes..."
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                                className="flex-1"
                              />
                              <Button
                                size="sm"
                                onClick={() => handleAddNotes(cancel._id)}
                                variant="outline"
                              >
                                Add Notes
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}

            {selectedTab === 'refunds' && (
              <div className="space-y-4">
                {refunds.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No refunds found</p>
                ) : (
                  refunds.map((refund) => (
                    <Card key={refund._id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg">{refund.userName}</h3>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                refund.status === 'completed' ? 'bg-green-100 text-green-700' :
                                refund.status === 'failed' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {refund.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{refund.userEmail}</p>
                            <p className="text-sm text-gray-700 mt-2">
                              <span className="font-medium">Amount:</span> ${refund.amount?.toFixed(2)}
                            </p>
                            <p className="text-sm text-gray-500">
                              Method: <span className="font-medium capitalize">{refund.method}</span>
                            </p>
                            <p className="text-sm text-gray-500">
                              Refund Date: {refund.refundDate ? format(new Date(refund.refundDate), 'PPp') : 'N/A'}
                            </p>
                          </div>
                          
                          {refund.status === 'pending' && (
                            <div className="flex gap-2 ml-4">
                              <Button
                                size="sm"
                                onClick={() => handleUpdateRefundStatus(refund._id, 'completed')}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Complete
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleUpdateRefundStatus(refund._id, 'failed')}
                                variant="destructive"
                              >
                                <AlertTriangle className="w-4 h-4 mr-1" />
                                Mark Failed
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Follow-up Modal */}
      {showFollowUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Follow Up with User</h2>
            <p className="text-sm text-gray-600 mb-4">
              Send a follow-up message to <strong>{selectedItem?.userName}</strong>
            </p>
            <textarea
              value={followUpMessage}
              onChange={(e) => setFollowUpMessage(e.target.value)}
              placeholder="Enter your follow-up message..."
              className="w-full p-3 border border-gray-300 rounded-lg mb-4 h-32"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowFollowUpModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleFollowUp} className="bg-blue-600 hover:bg-blue-700">
                Send Follow-Up
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Process Refund</h2>
            <p className="text-sm text-gray-600 mb-4">
              Process refund for <strong>{selectedItem?.userName}</strong>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Refund Amount ($)</label>
                <Input
                  type="number"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder="9.99"
                  step="0.01"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Refund Method</label>
                <select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value="stripe">Stripe</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-6">
              <Button variant="outline" onClick={() => setShowRefundModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleProcessRefund} className="bg-orange-600 hover:bg-orange-700">
                Process Refund
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCancellationsRefunds;
